import { getTierByGraphemeDict } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";
import {
  type GraphemeTaiwanTbclSet,
  type GraphemeTaiwanUsageMap,
  taiwanUsageClassificationBand,
} from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import type { GraphemeComponentPrevalenceLookup } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.decomposition";
import { isCjkStroke } from "@-/dev.improve.haniverse.lib.unicode";

/**
 * How widely a grapheme is used, from most to least prevalent.
 *
 * - `common`: PRC 通用规范汉字表 tier 1, or (traditional) any glyph carrying a TBCL curriculum level.
 * - `uncommon`: PRC tier 2, or a Taiwan MOE 常用字 with no TBCL level.
 * - `rare`: PRC tier 3, or a Taiwan MOE 次常用字.
 * - `obscure`: not on the PRC table, or a Taiwan MOE 罕用字 / a glyph absent from every chart.
 */
export type GraphemePrevalence = "common" | "uncommon" | "rare" | "obscure";

export type GraphemePrevalenceResult = {
  asCharacter: GraphemePrevalence;
  asPrimitive: GraphemePrevalence;
};

export type GetGraphemePrevalenceOptions = {
  /** Decomposition-graph closure for simplified script, seeded from PRC tier data. */
  simplifiedComponentPrevalenceLookup: GraphemeComponentPrevalenceLookup;
  /** Decomposition-graph closure for traditional script, seeded from {@link twUsageMap} + {@link twTbclSet}. */
  twComponentPrevalenceLookup: GraphemeComponentPrevalenceLookup;
  /**
   * Traditional glyphs carrying a TBCL curriculum level — the traditional `common` band. Paired
   * with {@link twUsageMap} so the three Taiwan MOE charts fill the `uncommon` / `rare` / `obscure`
   * bands beneath it. Empty until the TW asset resolves, which reads every 常用字 back as
   * `"uncommon"`.
   */
  twTbclSet: GraphemeTaiwanTbclSet;
  /**
   * Per-glyph Taiwan MOE classification map. Empty until the TW usage query resolves; until then
   * every traditional grapheme reads back as `"obscure"` (matching the fallback behaviour the
   * font-weight path uses while the map is loading).
   */
  twUsageMap: GraphemeTaiwanUsageMap;
};

function firstScalarCharacter(grapheme: string) {
  const codePoint = grapheme.codePointAt(0);
  return codePoint === undefined ? grapheme : String.fromCodePoint(codePoint);
}

/**
 * Bare CJK stroke primitives (e.g. 丿) are drawing strokes, not written characters. Some chart
 * sources list them under "rarely-used", which surfaced them as "Rare" on the card UI, but a
 * lone stroke should never be presented as a real-word grapheme — force them to "obscure".
 *
 * Covers the CJK Strokes block (U+31C0–U+31EF) plus the stroke ideographs in the U+4E00 family
 * that are pure strokes (not standalone radicals/characters like 一, 丨, 丶, 乙, 亅).
 */
const STROKE_PRIMITIVE_CHARACTERS: ReadonlySet<string> = new Set([
  "丿", // U+4E3F (撇, P)
  "㇒", // U+31D2 (撇, P) — also in the CJK Strokes block; listed for clarity
  "乀", // U+4E40
  "乁", // U+4E41
  "乚", // U+4E5A
  "乛", // U+4E5B
]);

function isStrokePrimitive(character: string): boolean {
  if (STROKE_PRIMITIVE_CHARACTERS.has(character)) {
    return true;
  }
  return isCjkStroke(character);
}

function simplifiedPrevalence(simplifiedGrapheme: string): GraphemePrevalence {
  const tier = getTierByGraphemeDict()[firstScalarCharacter(simplifiedGrapheme)];
  if (tier === 1) {
    return "common";
  }
  if (tier === 2) {
    return "uncommon";
  }
  if (tier === 3) {
    return "rare";
  }
  return "obscure";
}

function traditionalPrevalence(
  traditionalGrapheme: string,
  twUsageMap: GraphemeTaiwanUsageMap,
  twTbclSet: GraphemeTaiwanTbclSet,
): GraphemePrevalence {
  // Shared source of truth: a TBCL glyph is common, then the three MOE charts map one-to-one onto
  // uncommon / rare / obscure (see taiwanUsageClassificationBand). Kept identical to the
  // compoundsCount bucket so a traditional glyph's card prevalence and its reverse-decomposition
  // bucket agree.
  const character = firstScalarCharacter(traditionalGrapheme);
  return taiwanUsageClassificationBand(twUsageMap[character], twTbclSet.has(character));
}

/**
 * A grapheme that is obscure as a character inherits the band of the most prevalent characters
 * it is a building block of (the lookup's three sets are disjoint, so the first hit is the best).
 */
function withComponentPrevalenceBoost(
  grapheme: string,
  prevalence: GraphemePrevalence,
  lookup: GraphemeComponentPrevalenceLookup,
): GraphemePrevalence {
  if (prevalence !== "obscure") {
    return prevalence;
  }
  const scalarGrapheme = firstScalarCharacter(grapheme);
  if (lookup.commonComponentSet.has(scalarGrapheme)) {
    return "common";
  }
  if (lookup.uncommonComponentSet.has(scalarGrapheme)) {
    return "uncommon";
  }
  if (lookup.rareComponentSet.has(scalarGrapheme)) {
    return "rare";
  }
  return prevalence;
}

/**
 * Resolve grapheme prevalence for the given script. Uses PRC tier data when
 * `hanziScript` is `"simplified"` and Taiwan MOE usage charts (plus the TBCL curriculum) when
 * `"traditional"`. `asCharacter` reflects the raw dict prevalence; `asPrimitive` additionally
 * bumps an `obscure` grapheme to the band of the most prevalent characters that list it as a
 * decomposition component (`common` for tier-1 / TBCL characters, `uncommon` for tier-2 / 常用字,
 * `rare` for tier-3 / 次常用字).
 *
 * Traditional-script callers must supply {@link GetGraphemePrevalenceOptions} — both the
 * per-glyph usage map and the decomposition lookup derived from it. Simplified-script callers
 * may pass an empty map; tier data is sourced from the bundled PRC table.
 */
export function getGraphemePrevalence(
  grapheme: string,
  hanziScript: string,
  options: GetGraphemePrevalenceOptions,
): GraphemePrevalenceResult {
  // A bare stroke primitive should never present as a real-word grapheme: pin `asCharacter`
  // to obscure regardless of how a chart lists it. `asPrimitive` is still derived from this
  // base so a stroke that anchors common characters can keep its building-block boost.
  const strokePrimitive = isStrokePrimitive(firstScalarCharacter(grapheme));

  if (hanziScript === "traditional") {
    const asCharacter = strokePrimitive
      ? "obscure"
      : traditionalPrevalence(grapheme, options.twUsageMap, options.twTbclSet);
    return {
      asCharacter,
      asPrimitive: withComponentPrevalenceBoost(
        grapheme,
        asCharacter,
        options.twComponentPrevalenceLookup,
      ),
    };
  }
  if (hanziScript === "simplified") {
    const asCharacter = strokePrimitive ? "obscure" : simplifiedPrevalence(grapheme);
    return {
      asCharacter,
      asPrimitive: withComponentPrevalenceBoost(
        grapheme,
        asCharacter,
        options.simplifiedComponentPrevalenceLookup,
      ),
    };
  }
  return { asCharacter: "obscure", asPrimitive: "obscure" };
}
